import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { bindRealtimeVisibilityPause } from "@/lib/realtime-visibility-pause";
import { REALTIME_PAUSE_ON_HIDDEN_ENABLED } from "@/lib/workspace-conversation-realtime-hub";
import { useToast } from "@/hooks/use-toast";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { ContactGroup, LeadGroupMembership } from "@/types/lead";

export function useContactGroups(workspaceId: string | null) {
  const [groups, setGroups] = useState<ContactGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchGroups = useCallback(async () => {
    if (!workspaceId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("contact_groups")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("name", { ascending: true });

      if (error) throw error;
      
      const typedData: ContactGroup[] = (data || []).map(item => ({
        id: item.id,
        workspace_id: item.workspace_id,
        name: item.name,
        type: item.type,
        description: item.description,
        website: item.website,
        industry: item.industry,
        size: item.size,
        metadata: item.metadata,
        created_at: item.created_at,
        updated_at: item.updated_at,
      }));
      
      setGroups(typedData);
    } catch (error: any) {
      console.error("Error fetching groups:", error);
      toast({
        title: "Erro ao carregar grupos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, toast]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Realtime subscription
  useEffect(() => {
    if (!workspaceId) return;

    const channelName = `contact-groups-${workspaceId}`;
    let channel: RealtimeChannel | null = null;
    const reconcileTimerRef = { current: null as ReturnType<typeof setTimeout> | null };

    const subscribeChannel = () => {
      if (REALTIME_PAUSE_ON_HIDDEN_ENABLED && typeof document !== "undefined" && document.hidden) {
        return;
      }
      if (channel) return;
      channel = subscribePostgresChannel(
        channelName,
        {
          event: "*",
          schema: "public",
          table: "contact_groups",
          filter: `workspace_id=eq.${workspaceId}`,
        },
        () => {
          fetchGroups();
        },
      );
    };

    const teardownChannel = () => {
      if (!channel) return;
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
      channel = null;
    };

    const reconcileGroups = () => {
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
      reconcileTimerRef.current = setTimeout(() => {
        fetchGroups();
      }, 300);
    };

    const unbind = bindRealtimeVisibilityPause({
      pauseEnabled: REALTIME_PAUSE_ON_HIDDEN_ENABLED,
      subscribe: subscribeChannel,
      teardown: teardownChannel,
      onReconcile: reconcileGroups,
    });

    return () => {
      unbind();
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
  }, [workspaceId, fetchGroups]);

  const createGroup = async (data: Partial<ContactGroup>) => {
    if (!workspaceId) return null;

    try {
      const { data: newGroup, error } = await supabase
        .from("contact_groups")
        .insert({
          workspace_id: workspaceId,
          name: data.name!,
          type: data.type || "empresa",
          description: data.description,
          website: data.website,
          industry: data.industry,
          size: data.size,
          metadata: data.metadata || {},
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Grupo criado",
        description: `O grupo "${data.name}" foi criado com sucesso.`,
      });

      return newGroup;
    } catch (error: any) {
      toast({
        title: "Erro ao criar grupo",
        description: error.message,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateGroup = async (id: string, data: Partial<ContactGroup>) => {
    try {
      const { error } = await supabase
        .from("contact_groups")
        .update(data)
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Grupo atualizado",
        description: "As alterações foram salvas com sucesso.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar grupo",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteGroup = async (id: string) => {
    // Optimistic update - remove immediately from UI
    const previousGroups = groups;
    setGroups(prev => prev.filter(g => g.id !== id));

    try {
      const { error } = await supabase
        .from("contact_groups")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Grupo excluído",
        description: "O grupo foi removido com sucesso.",
      });

      return true;
    } catch (error: any) {
      // Rollback on error
      setGroups(previousGroups);
      toast({
        title: "Erro ao excluir grupo",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    groups,
    loading,
    createGroup,
    updateGroup,
    deleteGroup,
    refetch: fetchGroups,
  };
}

export function useLeadGroups(leadId: string | null) {
  const [memberships, setMemberships] = useState<LeadGroupMembership[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchMemberships = useCallback(async () => {
    if (!leadId) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("lead_group_memberships")
        .select(`
          *,
          group:group_id (
            id,
            name,
            type,
            website,
            industry,
            size
          )
        `)
        .eq("lead_id", leadId);

      if (error) throw error;
      
      const typedData: LeadGroupMembership[] = (data || []).map((item: any) => ({
        id: item.id,
        lead_id: item.lead_id,
        group_id: item.group_id,
        role: item.role,
        department: item.department,
        is_primary: item.is_primary || false,
        metadata: item.metadata,
        created_at: item.created_at,
        group: item.group ? {
          id: item.group.id,
          workspace_id: '',
          name: item.group.name,
          type: item.group.type,
          website: item.group.website,
          industry: item.group.industry,
          size: item.group.size,
          created_at: '',
        } : undefined,
      }));
      
      setMemberships(typedData);
    } catch (error: any) {
      console.error("Error fetching lead groups:", error);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    fetchMemberships();
  }, [fetchMemberships]);

  // Realtime subscription
  useEffect(() => {
    if (!leadId) return;

    const channelName = `lead-groups-${leadId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "lead_group_memberships",
        filter: `lead_id=eq.${leadId}`,
      },
      () => {
        fetchMemberships();
      },
    );

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [leadId, fetchMemberships]);

  const addToGroup = async (groupId: string, data: { role?: string; department?: string; is_primary?: boolean }) => {
    if (!leadId) return null;

    try {
      const { data: membership, error } = await supabase
        .from("lead_group_memberships")
        .insert({
          lead_id: leadId,
          group_id: groupId,
          role: data.role,
          department: data.department,
          is_primary: data.is_primary || false,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Lead adicionado ao grupo",
        description: "O lead foi vinculado ao grupo com sucesso.",
      });

      return membership;
    } catch (error: any) {
      if (error.code === '23505') {
        toast({
          title: "Lead já está no grupo",
          description: "Este lead já pertence a este grupo.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro ao adicionar ao grupo",
          description: error.message,
          variant: "destructive",
        });
      }
      return null;
    }
  };

  const updateMembership = async (membershipId: string, data: Partial<LeadGroupMembership>) => {
    try {
      const { error } = await supabase
        .from("lead_group_memberships")
        .update({
          role: data.role,
          department: data.department,
          is_primary: data.is_primary,
        })
        .eq("id", membershipId);

      if (error) throw error;

      toast({
        title: "Vínculo atualizado",
        description: "As informações foram atualizadas.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao atualizar",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const removeFromGroup = async (membershipId: string) => {
    try {
      const { error } = await supabase
        .from("lead_group_memberships")
        .delete()
        .eq("id", membershipId);

      if (error) throw error;

      toast({
        title: "Lead removido do grupo",
        description: "O vínculo foi removido com sucesso.",
      });

      return true;
    } catch (error: any) {
      toast({
        title: "Erro ao remover do grupo",
        description: error.message,
        variant: "destructive",
      });
      return false;
    }
  };

  const primaryGroup = memberships.find(m => m.is_primary);

  return {
    memberships,
    loading,
    primaryGroup,
    addToGroup,
    updateMembership,
    removeFromGroup,
    refetch: fetchMemberships,
  };
}
